// ============================================================================
// Doc Order — §2 vesting: trust/LLC surface ONLY (data.js DO_VESTING port).
// Marital-status + Manner-of-Title live in the split-off Vesting Wizard
// project (not in this bundle) — individual vesting is entered manually.
// ============================================================================

import type { VestingWorkflowState } from './types.ts';

export const VESTING_TYPES = [
  'Individual', 'Co-signer', 'Title only', 'Non Title Spouse', 'Trustee',
  'Title Only Trustee', 'Settlor Trustee', 'Settlor', 'Title Only Settlor Trustee', 'Officer',
] as const;

export const TRUST_ORG_TYPES = [
  'An Inter Vivos Trust', 'A California Trust', 'A Trust', 'A Revocable Living Trust', 'An Irrevocable Trust',
] as const;

/** US state names as Encompass stores them (Field 1860 — full names; DC per legacy picker). */
export const DO_US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Dist. of Col.', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois',
  'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
  'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana',
  'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
] as const;

const DO_US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
  'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
  'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
] as const;

function normalizeStateCode(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  const index = DO_US_STATES.findIndex((state) => state.toUpperCase() === upper);
  return index >= 0 ? DO_US_STATE_CODES[index] : upper;
}

/** Soft LLC review rule: flag only when the entity and subject property are
 * in different states. Encompass may supply Field 14 as a two-letter code
 * while Field 1860 uses the full name, so compare their normalized codes. */
export function entityStateDiffersFromProperty(
  entityState: string | null | undefined,
  propertyState: string | null | undefined
): boolean {
  const entity = normalizeStateCode(entityState);
  const property = normalizeStateCode(propertyState);
  return !!(entity && property && entity !== property);
}

/** Org Types for Field 1861 (full vesting clause values — drop into legal docs
 *  verbatim). R4 #19: Jennifer struck the 3 trust entries (trusts have their
 *  own TRUST_ORG_TYPES picker) and the 8 banking/federal entries (lender org
 *  types, never a borrower LLC) — 10 remain. */
export const DO_LLC_ORG_TYPES = [
  'A California Corporation',
  'A California General Partnership', 'A California Limited Partnership',
  'A California Limited Liability Company', 'A Corporation', 'A General Partnership',
  'A Sole Proprietorship', 'A Limited Partnership', 'A Partnership',
  'A Limited Liability Company',
] as const;

export const LLC_ORG_TYPES = DO_LLC_ORG_TYPES.filter((t) =>
  /Liability|Corporation|Partnership|Proprietorship|Association|Bank|Credit Union/.test(t)
);

export type ClosingType = 'individual' | 'title-only' | 'trust' | 'llc';

const RESTRICT: Record<ClosingType, { types: string[] }> = {
  individual: { types: ['Individual', 'Co-signer', 'Non Title Spouse'] },
  'title-only': { types: ['Title only', 'Title Only Trustee', 'Title Only Settlor Trustee'] },
  trust: { types: ['Trustee', 'Settlor Trustee', 'Settlor', 'Title Only Trustee', 'Title Only Settlor Trustee'] },
  llc: { types: ['Officer'] },
};

export function closingTypeOf(
  v: Pick<VestingWorkflowState, 'closingInTrust' | 'closingInLlc' | 'titleOnlyBorrower'> | null | undefined
): ClosingType {
  if (!v) return 'individual';
  if (v.closingInTrust) return 'trust';
  if (v.closingInLlc) return 'llc';
  if (v.titleOnlyBorrower) return 'title-only';
  return 'individual';
}

export function allowedVestingTypes(closingType: ClosingType): { types: string[] } {
  return RESTRICT[closingType] || RESTRICT.individual;
}

/** Entity → auto-applied vesting types (Trust → Settlor Trustee; LLC → Officer for all). */
export function derivedTypes(entity: ClosingType): { primary: string; titleOnly: string; mannerNA: boolean } {
  if (entity === 'trust') {
    return { primary: 'Settlor Trustee', titleOnly: 'Title Only Settlor Trustee', mannerNA: false };
  }
  if (entity === 'llc') return { primary: 'Officer', titleOnly: 'Officer', mannerNA: true };
  return { primary: 'Individual', titleOnly: 'Non Title Spouse', mannerNA: false };
}

/**
 * Compose the processor Doc Order's "Final Vesting To Read" comparison value.
 * Field 1867 remains read-only Encompass source data. Trust + LLC auto-build;
 * individual/title-only is entered manually until the Vesting Wizard ships,
 * so the current clause is returned unchanged.
 */
export function buildFinalVesting(v: VestingWorkflowState, currentVesting: string | null): string {
  const ct = closingTypeOf(v);
  if (ct === 'trust') {
    let s = (v.trustName || 'Trust').trim();
    if (v.trustOrgType) s += `, ${v.trustOrgType}`;
    if (v.trustDate) s += `, dated ${v.trustDate}`;
    if (v.trustAmendedDate) s += `, as amended ${v.trustAmendedDate}`;
    return s;
  }
  if (ct === 'llc') return (v.llcEntityName || '').trim();
  return (currentVesting || '').trim();
}

/**
 * Wizard Save gate (README §2): a title-only answer, an entity decision, and a
 * non-empty final vesting clause must all exist. Save is what flips
 * vesting_confirmed=true (completion-driven — no manual "Confirmed" toggle).
 */
export function vestingSaveReady(v: VestingWorkflowState, finalClause: string): boolean {
  // null = the title-only question was never answered — Save stays locked.
  const titleOnlyAnswered =
    v.titleOnlyBorrower === false || (v.titleOnlyBorrower === true && !!(v.titleOnlyRole && v.titleOnlyName));
  const entityAnswered = v.closingInTrust || v.closingInLlc || v.entityNeither;
  return titleOnlyAnswered && entityAnswered && !!finalClause.trim();
}
